import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Vote, Trash2, Plus, Save, Pencil, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', party: '', email: '', order: 0 };

export const AdminCandidates = ({ token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [candidates, setCandidates] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const load = async () => {
    const [c, s, b] = await Promise.all([
      axios.get(`${API}/candidates`),
      axios.get(`${API}/settings/interpellation_subject`),
      axios.get(`${API}/settings/interpellation_body`),
    ]);
    setCandidates(c.data);
    setSubject(s.data.value);
    setBody(b.data.value);
  };

  useEffect(() => { load().catch(() => toast.error('Erreur de chargement des candidats')); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editingId) await axios.put(`${API}/candidates/${editingId}`, payload, auth);
      else await axios.post(`${API}/candidates`, payload, auth);
      toast.success(editingId ? 'Candidat mis à jour' : 'Candidat ajouté');
      setForm(EMPTY);
      setEditingId(null);
      load().catch(() => {});
    } catch (err) {
      toast.error(err.response?.status === 422 ? 'Email invalide' : "Erreur lors de l'enregistrement");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer ${c.name} ?`)) return;
    try {
      await axios.delete(`${API}/candidates/${c.id}`, auth);
      toast.success('Candidat supprimé');
      load().catch(() => {});
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const edit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, party: c.party || '', email: c.email, order: c.order || 0 });
  };

  const saveTemplate = async () => {
    try {
      await Promise.all([
        axios.put(`${API}/settings/interpellation_subject`, { value: subject }, auth),
        axios.put(`${API}/settings/interpellation_body`, { value: body }, auth),
      ]);
      toast.success("Message d'interpellation enregistré");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  return (
    <Card className="mt-6" data-testid="admin-candidates-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Vote className="w-5 h-5" />Candidats à interpeller (2027)</CardTitle>
        <CardDescription>Liste affichée sur la page pétition et message pré-rempli envoyé par les visiteurs.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        <form onSubmit={save} className="grid md:grid-cols-[1fr_1fr_1fr_80px_auto] gap-3 items-end">
          <div><Label>Nom *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="candidate-input-name" /></div>
          <div><Label>Parti / mouvement</Label><Input value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} data-testid="candidate-input-party" /></div>
          <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="candidate-input-email" /></div>
          <div><Label>Ordre</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="candidate-input-order" /></div>
          <div className="flex gap-2">
            <Button type="submit" className="gap-1" data-testid="candidate-save-btn">
              {editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}{editingId ? 'Modifier' : 'Ajouter'}
            </Button>
            {editingId && (
              <Button type="button" variant="ghost" size="icon" onClick={() => { setEditingId(null); setForm(EMPTY); }} data-testid="candidate-cancel-edit-btn"><X className="w-4 h-4" /></Button>
            )}
          </div>
        </form>

        {candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="candidates-empty">Aucun candidat pour le moment.</p>
        ) : (
          <ul className="divide-y divide-border border rounded-lg" data-testid="candidates-list">
            {candidates.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`candidate-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium">{c.name} {c.party && <span className="text-muted-foreground font-normal">· {c.party}</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => edit(c)} data-testid={`candidate-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`candidate-delete-${c.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-3 pt-4 border-t">
          <h4 className="font-semibold">Message d'interpellation par défaut</h4>
          <div><Label>Objet</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="interpellation-subject-input" /></div>
          <div><Label>Texte</Label><Textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} data-testid="interpellation-body-input" /></div>
          <p className="text-xs text-muted-foreground">Le nom du visiteur est ajouté automatiquement en signature. Les visiteurs peuvent modifier le texte avant envoi.</p>
          <Button onClick={saveTemplate} className="gap-2" data-testid="interpellation-save-btn"><Save className="w-4 h-4" />Enregistrer le message</Button>
        </div>
      </CardContent>
    </Card>
  );
};
